import { formatDate } from '@angular/common';
import { translate } from '@jsverse/transloco';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ENV_CONFIG, EnvironmentConfig, IJsonPatchModel, PageResponse } from '@libs/vc-core-lib';
import { map, Observable, take } from 'rxjs';
import { IMssOrgSearchDTO } from '../dto/mss-org.search.dto';
import { Role } from '../enum/role.enum';
import { IContactInfo } from '../model/contact-info.model';
import { IExporterPayloadMssOrgRequest } from '../model/exporter-mss-org-request.model';
import { IMssOrgDetailModel } from '../model/mss-org-detail.model';
import { IMssOrgModel } from '../model/mss-org.model';

@Injectable({
    providedIn: 'root',
})
export class MSSOrgService {
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _environment: EnvironmentConfig = inject(ENV_CONFIG);
    private readonly _mssOrgPath: string = 'mss-org';

    getContactInfo(orgId: string): Observable<IContactInfo[]> {
        return this._httpClient.get<IContactInfo[]>(
            `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/${orgId}/contact-info`
        );
    }

    createContactInfo(newContact: IContactInfo, orgId: string): Observable<IContactInfo> {
        return this._httpClient.post<IContactInfo>(
            `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/${orgId}/contact-info`,
            newContact
        );
    }

    deleteContactInfo(orgId: string, username: string, role: Role): Observable<void> {
        return this._httpClient.delete<void>(
            `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/${orgId}/contact-info/${username}/${role}`
        );
    }

    getOrgByVCOrgId(orgId: string): Observable<IMssOrgModel> {
        return this._httpClient.get<IMssOrgModel>(
            `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/org/${orgId}`
        );
    }

    getOrgsByIds(orgIds: string[]): Observable<IMssOrgModel[]> {
        return this._httpClient.post<IMssOrgModel[]>(
            `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/orgByIds`,
            orgIds
        );
    }

    patchOrg(orgId: string, models: Array<IJsonPatchModel>): Observable<IMssOrgModel> {
        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json-patch+json');
        const httpOptions = {
            headers: headers,
        };
        return this._httpClient.patch<IMssOrgModel>(
            `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/${orgId}`,
            models,
            httpOptions
        );
    }

    public search(searchDTO: IMssOrgSearchDTO): Observable<PageResponse<IMssOrgDetailModel>> {
        return this._httpClient.post<PageResponse<IMssOrgDetailModel>>(
            `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/searchPage`,
            searchDTO
        );
    }

    public exportMssOrgSearch(exporterPayload: IExporterPayloadMssOrgRequest): Observable<void> {
        const today = formatDate(new Date(Date.now()), 'yyyy-MM-dd', 'en-US');
        const name = translate('MSS.MANAGED_CONNECTIVITY.TABS.ALL_SITES');
        const fileName = `${name}_${today}.csv`;
        return this._httpClient
            .post<Blob>(
                `${this._environment.APP.VC_MSS_COMMON_SERVICE_BASE_URL}/${this._mssOrgPath}/searchPage/export`,
                exporterPayload,
                {
                    observe: 'response',
                    responseType: 'blob' as 'json',
                }
            )
            .pipe(
                take(1),
                map((response: HttpResponse<Blob>) => {
                    if (!response) return;
                    const downloadURL = URL.createObjectURL(response.body!);
                    const link = document.createElement('a');
                    link.href = downloadURL;
                    link.download = response.headers.get('filename') ?? fileName;
                    link.click();
                    link.remove();
                })
            );
    }
}
